import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ReceivingShipmentService } from '../../../service/api/receiving-shipment-service';
import { redux } from 'Core/redux';

export const STORE_KEY = 'receivingShipment';

export const INITIAL_STATE = {
  data: [],
  loading: false,
  error: null,
  filterVisible: false,
  filters: {
    /** Header search — part number / PO number (server-side). */
    searchText: '',
    /** Toolbar search — supplier name / shipment number (server-side). */
    pageSearchText: '',
    supplier: null,
    status: null,
    shipmentDate: null,
  },
};

const buildListFilterPayload = (filters = {}) => {
  const headerSearch = String(filters.searchText ?? '').trim();
  const pageSearch = String(filters.pageSearchText ?? '').trim();
  const supplierName =
    filters.supplier?.searchText ||
    filters.supplier?.label ||
    filters.supplier?.value ||
    '';
  const statusVal = filters.status?.value;

  const payload = {
    searchText: pageSearch,
    partNumber: headerSearch,
    poNumber: headerSearch,
    supplierName: String(supplierName).trim(),
    shipmentDate: filters.shipmentDate || '',
  };

  if (statusVal && String(statusVal).trim() !== '') {
    payload.status = String(statusVal).trim();
  }

  return payload;
};

export const fetchReceivingShipments = createAsyncThunk(
  'receivingShipment/fetchReceivingShipments',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await ReceivingShipmentService.getAllShipments(filters);
      if (response && response.status === 200) {
        return response.response || [];
      }
      return rejectWithValue(response?.errorMessage?.message || 'Failed to load receiving shipments');
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to load receiving shipments');
    }
  },
);

export const fetchReceivingShipmentsWithFilters = (filterOverrides = {}) => (dispatch, getState) => {
  const filters = {
    ...getState()[STORE_KEY]?.filters,
    ...filterOverrides,
  };
  dispatch(fetchReceivingShipments(buildListFilterPayload(filters)));
};

export const { reducer, actions } = createSlice({
  name: STORE_KEY,
  initialState: INITIAL_STATE,
  reducers: {
    setFilterVisible: (state, action) => {
      state.filterVisible = action.payload;
    },
    setSearchText: (state, action) => {
      state.filters.searchText = action.payload;
    },
    setPageSearchText: (state, action) => {
      state.filters.pageSearchText = action.payload;
    },
    setSupplier: (state, action) => {
      state.filters.supplier = action.payload;
    },
    setStatus: (state, action) => {
      state.filters.status = action.payload;
    },
    setShipmentDate: (state, action) => {
      state.filters.shipmentDate = action.payload;
    },
    resetFilters: state => {
      state.filters = INITIAL_STATE.filters;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchReceivingShipments.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchReceivingShipments.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload || [];
      })
      .addCase(fetchReceivingShipments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.data = [];
      });
  },
});

redux.injectReducer(STORE_KEY, reducer);
